import { useAuth0 } from "@auth0/auth0-react";
import { Alert, AlertIcon, Button, Spinner } from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { FinalizeJoinByCommunityInviteStatus } from "src/__generated__/graphql";
import {
	clearStoredCommunityInviteSession,
	getStoredCommunityInviteSession,
	storeCommunityInviteSession,
} from "src/features/communities/invite-session";
import type { StoredCommunityInviteSession } from "src/features/communities/invite-session.types";
import { FINALIZE_JOIN_BY_COMMUNITY_INVITE } from "src/features/communities/graphql";

const PRIMARY_BUTTON_PROPS = {
	borderRadius: "0",
	border: "1px solid #ED6E5C",
	bg: "#ED6E5C",
	color: "white",
	boxShadow: "none",
	_hover: { bg: "#D95D4C", borderColor: "#D95D4C" },
	_active: { bg: "#C95242", borderColor: "#C95242" },
};

const OUTLINE_BUTTON_PROPS = {
	borderRadius: "0",
	border: "1px solid #ED6E5C",
	bg: "transparent",
	color: "#ED6E5C",
	boxShadow: "none",
	_hover: { bg: "#FFF3F0" },
	_active: { bg: "#FBE2DE" },
};

const getInvitePortalUrl = (inviteToken?: string) => {
	const baseUrl = `${window.location.origin}/community-invite`;

	if (!inviteToken) {
		return baseUrl;
	}

	return `${baseUrl}?token=${encodeURIComponent(inviteToken)}`;
};

export default function CommunityInviteCallbackPage() {
	const { isAuthenticated, isLoading, logout, user } = useAuth0();
	const navigate = useNavigate();
	const hasFinalizedRef = useRef(false);

	const [storedSession, setStoredSession] = useState<
		StoredCommunityInviteSession | null
	>(() => getStoredCommunityInviteSession());
	const [status, setStatus] = useState<
		"idle" | "finalizing" | "mismatch" | "expired" | "invalid" | "error"
	>("idle");
	const [message, setMessage] = useState<string | null>(null);

	const [finalizeJoinByCommunityInvite, { loading: finalizeLoading }] =
		useMutation(FINALIZE_JOIN_BY_COMMUNITY_INVITE);

	useEffect(() => {
		setStoredSession(getStoredCommunityInviteSession());
	}, []);

	useEffect(() => {
		if (isLoading || storedSession) {
			return;
		}

		setStatus("invalid");
		setMessage(
			"This callback page does not have a stored invite session yet. Start again from the community invite link."
		);
	}, [isLoading, storedSession]);

	const invitePortalUrl = useMemo(
		() => getInvitePortalUrl(storedSession?.inviteToken),
		[storedSession?.inviteToken]
	);

	useEffect(() => {
		if (isLoading || !isAuthenticated || !storedSession) {
			return;
		}

		if (hasFinalizedRef.current) {
			return;
		}

		if (!user?.email) {
			hasFinalizedRef.current = true;
			setStatus("mismatch");
			setMessage(
				"We could not confirm the account on this browser session. Please log out and try again with the email address tied to your invite."
			);
			return;
		}

		hasFinalizedRef.current = true;
		setStatus("finalizing");
		setMessage(null);

		finalizeJoinByCommunityInvite({
			variables: {
				sessionToken: storedSession.inviteJoinSessionToken,
			},
		})
			.then((response) => {
				const result = response.data?.finalizeJoinByCommunityInvite;

				if (!result) {
					throw new Error("Invite finalize did not return a result.");
				}

				if (result.status === FinalizeJoinByCommunityInviteStatus.Success) {
					const nextRoute = result.redirectTo || "/apply";

					if (nextRoute === "/apply") {
						storeCommunityInviteSession({
							...storedSession,
							organizationReferralCode:
								result.organizationReferralCode ||
								storedSession.organizationReferralCode,
						});
						window.localStorage.removeItem("redirect");
					} else {
						clearStoredCommunityInviteSession();
					}

					navigate(nextRoute, { replace: true });
					return;
				}

				if (result.status === FinalizeJoinByCommunityInviteStatus.Mismatch) {
					setStatus("mismatch");
					setMessage(
						`This invite was started for ${storedSession.expectedEmail}, but you are signed in as ${user.email}.`
					);
					return;
				}

				clearStoredCommunityInviteSession();
				setStatus(
					result.status === FinalizeJoinByCommunityInviteStatus.Expired
						? "expired"
						: "invalid"
				);
				setMessage(
					result.status === FinalizeJoinByCommunityInviteStatus.Expired
						? "This invite session expired before sign-in completed. Start again from the invite link."
						: "This invite session is no longer valid. Start again from the latest invite link."
				);
			})
			.catch((error: Error) => {
				hasFinalizedRef.current = false;
				setStatus("error");
				setMessage(error.message || "We could not complete the invite flow.");
			});
	}, [
		finalizeJoinByCommunityInvite,
		isAuthenticated,
		isLoading,
		navigate,
		storedSession,
		user?.email,
	]);

	const retryFromInvite = () => {
		clearStoredCommunityInviteSession();
		navigate(invitePortalUrl, { replace: true });
	};

	const restartWithDifferentAccount = () => {
		logout({
			returnTo: invitePortalUrl,
		});
	};

	const shouldShowLoader =
		isLoading ||
		(Boolean(storedSession) &&
			(status === "idle" || status === "finalizing" || finalizeLoading));

	const hasRecoveryState =
		status === "mismatch" ||
		status === "expired" ||
		status === "invalid" ||
		status === "error";

	const hasSession = Boolean(storedSession);
	const showRecoveryCard = hasSession && hasRecoveryState;
	const heading =
		storedSession?.organizationName || "Your community invite is loading";
	return (
		<>
			<Helmet>
				<title>Finish invite | LaborHack Pro</title>
			</Helmet>
			<div className="relative min-h-screen overflow-hidden bg-[#061018] text-slate-50">
				<div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(237,110,92,0.25),_transparent_30%),radial-gradient(circle_at_80%_30%,_rgba(255,255,255,0.1),_transparent_22%),linear-gradient(145deg,_#061018_0%,_#0a1a24_48%,_#04090d_100%)]" />
				<div className="absolute inset-0 opacity-[0.16] [background-image:linear-gradient(rgba(255,255,255,0.14)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.14)_1px,transparent_1px)] [background-size:84px_84px]" />

				<div className="relative mx-auto flex min-h-screen max-w-5xl items-center px-6 py-16 sm:px-10">
					<motion.div
						initial={{ opacity: 0, y: 16 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.45 }}
						className="w-full"
					>
						<div className="mb-6 inline-flex items-center gap-2 border border-white/15 bg-white/8 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-200">
							<span className="h-2 w-2 bg-brand-400" />
							Checking your invite
						</div>

						<div className="border border-white/12 bg-white/95">
							<div className="bg-[#f7f9fa] px-6 py-7 text-slate-900 sm:px-8 sm:py-8">
								<div className="mb-6 flex flex-wrap items-start justify-between gap-4">
									<div>
										<div className="inline-flex border border-[#D7D7D7] bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#4A4A4A]">
											{status === "mismatch"
												? "Account mismatch"
												: status === "expired"
													? "Session expired"
													: status === "invalid"
														? "Invite invalid"
														: "Finishing invite"}
										</div>
										<h1 className="mt-4 font-[AeonikPro] text-[1.75rem] font-bold leading-tight text-[#121212] lg:text-[2rem]">
											{heading}
										</h1>
										<p className="mt-2 max-w-2xl text-sm leading-6 text-[#666666] sm:text-base">
											{shouldShowLoader
												? "We're confirming your details and taking you to the next step."
												: "Your invite is ready. Use the options below to continue."}
										</p>
									</div>
								</div>

								{shouldShowLoader ? (
									<div className="flex min-h-[260px] flex-col items-center justify-center border border-[#E7E7E7] bg-white px-6 text-center">
										<Spinner color="brand.500" size="xl" />
										<p className="mt-6 text-base font-medium text-prussianBlue-500">
											Finishing your invite
										</p>
										<p className="mt-2 max-w-md text-sm leading-6 text-[#666666]">
											Do not close this tab while we confirm your account and move
											you forward.
										</p>
									</div>
								) : showRecoveryCard ? (
									<div className="space-y-5">
										{message ? (
											<Alert
												status={status === "mismatch" ? "warning" : "error"}
												className="border border-[#E7E0DA] bg-white"
												borderRadius="0"
											>
												<AlertIcon />
												{message}
											</Alert>
										) : null}

										<div className="space-y-4">
											<div className="border border-[#E7E7E7] bg-white px-4 py-4">
												<p className="text-xs uppercase tracking-[0.22em] text-[#7A7A7A]">
													Invite email
												</p>
												<p className="mt-2 text-sm font-medium text-[#121212]">
													{storedSession?.expectedEmail}
												</p>
											</div>
										</div>

										{status === "mismatch" ? (
											<div className="flex flex-col gap-3 sm:flex-row">
												<Button
													size="lg"
													onClick={restartWithDifferentAccount}
													{...PRIMARY_BUTTON_PROPS}
												>
													Log out and try again
												</Button>
												<Button
													as={RouterLink}
													to={invitePortalUrl}
													size="lg"
													{...OUTLINE_BUTTON_PROPS}
												>
													Back to invite
												</Button>
											</div>
										) : (
											<div className="flex flex-col gap-3 sm:flex-row">
												<Button
													size="lg"
													onClick={retryFromInvite}
													{...PRIMARY_BUTTON_PROPS}
												>
													Restart invite
												</Button>
												<Button
													as={RouterLink}
													to={invitePortalUrl}
													size="lg"
													{...OUTLINE_BUTTON_PROPS}
												>
													Open invite portal
												</Button>
											</div>
										)}
									</div>
								) : (
									<div className="flex min-h-[220px] flex-col items-center justify-center border border-[#E7E7E7] bg-white px-6 text-center">
										<p className="text-base font-medium text-prussianBlue-500">
											No invite session found
										</p>
										<p className="mt-2 max-w-md text-sm leading-6 text-[#666666]">
											{message ||
												"Open the latest invite link again to continue."}
										</p>
										<Button
											as={RouterLink}
											to={invitePortalUrl}
											className="mt-5"
											{...PRIMARY_BUTTON_PROPS}
										>
											Go to invite portal
										</Button>
									</div>
								)}
							</div>
						</div>
					</motion.div>
				</div>
			</div>
		</>
	);
}
